import toString from 'lodash/toString'
import { INCOTERM_OPTIONS } from '@klear/constants/options'
import { ORDER_EVENT_OPERATIONAL_TITLES } from '@klear/order-event/constants'
import { EventFieldGrid, type EventField } from '@klear/order-event-ui/event-forms/event-field-grid'
import { dateFormatted, type DateDisplayInput } from '@klear/order-event/list-display'
import { useSuppliers } from '@klear/ui/hooks/use-suppliers'
import {
  EventCombobox,
  EventComboboxWithCustomInput,
  EventDatePicker,
  EventFileInput,
  EventDocumentList,
  EventInput,
  EventMoneyInput,
  EventTextarea
} from '@klear/order-event-ui/event-shared/event-form-control'
import { Money } from '@klear/money'

interface OperationalFormProps {
  traderId: number
}

export function OperationalForm({ traderId }: OperationalFormProps) {
  const { suppliers } = useSuppliers(traderId)
  const incotermOptions = [...INCOTERM_OPTIONS]

  return (
    <>
      <EventFileInput label="Upload File(s)" name="files" multiple />
      <EventDocumentList />
      <div className="grid grid-cols-2 gap-4">
        <EventComboboxWithCustomInput
          label="Title"
          name="title"
          options={ORDER_EVENT_OPERATIONAL_TITLES.map((t) => ({ label: t, value: t }))}
        />
        <EventInput label="Reference Number" name="number" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <EventCombobox
          label="Supplier"
          name="supplierId"
          options={suppliers.map((s) => ({
            label: s.name,
            value: toString(s.id)
          }))}
        />
        <EventCombobox label="Incoterm" name="incoterm" options={incotermOptions} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <EventMoneyInput name="amount" label="Amount" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <EventDatePicker label="Start Date" name="effectiveDate" />
        <EventDatePicker label="Delivery Date" name="endDate" />
      </div>
      <EventTextarea label="Notes" name="notes" />
    </>
  )
}

type OperationalViewProps = {
  operational: {
    supplierId?: string | number
    title: string
    number?: string
    incoterm?: string
    effectiveDate: DateDisplayInput
    endDate?: DateDisplayInput
    amount?: Money
  }
  traderId: number
}

export function OperationalView({ traderId, operational }: OperationalViewProps) {
  const { suppliers } = useSuppliers(traderId)
  const supplier = suppliers.find((s) => s.id === operational.supplierId)

  const fields: EventField[] = []
  if (operational.amount != null) fields.push({ label: 'Amount', value: new Money(operational.amount).toString() })
  if (operational.title) fields.push({ label: '', value: operational.title })
  if (operational.supplierId != null) fields.push({ label: 'Supplier', value: supplier?.name ?? null })
  if (operational.endDate) {
    fields.push({ label: 'Start Date', value: dateFormatted(operational.effectiveDate) })
    fields.push({ label: 'End Date', value: dateFormatted(operational.endDate) })
  }
  if (operational.incoterm) fields.push({ label: 'Incoterm', value: operational.incoterm })

  return <EventFieldGrid fields={fields} />
}
